import axios, { AxiosError, AxiosInstance } from "axios";

export const baseURL = import.meta.env.VITE_API_BASE_URL || "";

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true, // refresh token cookie (httpOnly)
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios
      .post(`${baseURL}/auth/web/refresh`, null, { withCredentials: true })
      .then((r) => {
        const token = (r.data as { access_token?: string }).access_token ?? null;
        if (token) accessToken = token;
        return token;
      })
      .catch(() => null)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config;
    if (status === 401 && original && !(original as { _retry?: boolean })._retry) {
      (original as { _retry?: boolean })._retry = true;
      const token = await refresh();
      if (token) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return api.request(original);
      }
      onUnauthorized?.();
    }
    return Promise.reject(error);
  }
);
